import { Box, type BoxT } from '@mentimeter/ragnar-ui/box';
import {
  Children,
  cloneElement,
  isValidElement,
  forwardRef,
  type ReactElement,
} from 'react';
import { calculateDotsGridDimensions } from './calculateDotsGridDimensions';
import {
  HORIZONTAL_GRID_GAP_PERCENTAGE,
  LAYOUT_MAP,
  VERTICAL_GRID_GAP_PERCENTAGE,
} from './constants';

interface ChildrenProps {
  width: number;
  height: number;
  dotRadius: number;
}

interface DotsGridProps extends BoxT {
  width: number;
  height: number;
  largestNumberOfDots?: number;
  children: ReactElement<ChildrenProps> | Array<ReactElement<ChildrenProps>>;
}

export const DotsGrid = forwardRef<HTMLDivElement, DotsGridProps>(
  ({ width, height, largestNumberOfDots = 1, children, ...rest }, ref) => {
    const childrenArray = Children.toArray(children);
    const count = childrenArray.length;

    const dimensions = calculateDotsGridDimensions({
      width,
      height,
      count,
      largestNumberOfDots,
    });

    if (!dimensions) {
      return null;
    }

    const { childWidth, childHeight, dotRadius } = dimensions;
    const layout = LAYOUT_MAP[count]!;

    const horizontalGap =
      width > 1000
        ? width * HORIZONTAL_GRID_GAP_PERCENTAGE
        : (width * HORIZONTAL_GRID_GAP_PERCENTAGE) / 2;

    const verticalGap = height * VERTICAL_GRID_GAP_PERCENTAGE;

    return (
      <Box
        ref={ref}
        display="flex"
        flexDirection="column"
        alignItems="center"
        width={width}
        height={height}
        gap={verticalGap}
        {...rest}
      >
        {layout.map((cols, rowIndex) => (
          <Box
            key={rowIndex}
            gap={horizontalGap}
            flexDirection="row"
            justifyItems="center"
            alignItems="center"
          >
            {childrenArray.splice(0, cols).map((child, index) =>
              isValidElement<ChildrenProps>(child)
                ? cloneElement(child, {
                    width: childWidth,
                    height: childHeight,
                    dotRadius,
                    key: index,
                  })
                : child,
            )}
          </Box>
        ))}
      </Box>
    );
  },
);
